// Sugalarm — spoken-transcript → intent grammar (cycle 383).
//
// PURE + DETERMINISTIC. No React, no network, no fuzzy matching, no LLM,
// no edit distance. A transcript either matches one of a finite, hand-
// written set of anchored patterns or it does not.
//
// FAIL CLOSED IS THE WHOLE SAFETY POSTURE: `parseTranscript` returns
// `null` for anything it does not recognize, and the caller does nothing.
// A voice layer that guesses is worse than one that misses — a wrong guess
// on `logInsulin` writes a dose nobody asked for. Every ambiguity in here
// resolves toward `null`.
//
// ─── THE INTENT SET CONTAINS NO ALARM CONTROL, BY CONSTRUCTION ──────────
// There is deliberately no snooze / silence / stop / acknowledge / dismiss
// intent, and there must never be one. Voice can ASK about glucose and LOG
// a dose; it can never quiet an alarm. That is enforced structurally (the
// intent cannot be expressed) rather than by a runtime check, and pinned by
// a test that greps the exported enum for alarm vocabulary. See
// `voiceGrammar.test.ts` → "the intent set contains no alarm control".
//
// The intent + slot contract is the cycle-101 one, carried forward
// verbatim so the answer layer (`voiceIntents.ts`) is reused unchanged:
// `slots.units` is ALWAYS a half-unit decimal string ("6", "6.5", "0.5").

// ─── intent contract ───────────────────────────────────────────────────

/** Every intent this grammar can produce. Exported as a value (not just a
 *  type) so a test can assert the set contains no alarm-control member. */
export const VOICE_INTENTS = [
  "getGlucose",
  "getIOB",
  "getInsulinTimeLeft",
  "getLastDose",
  "logInsulin",
  "confirmYes",
  "confirmNo",
] as const;

export type VoiceIntentName = (typeof VOICE_INTENTS)[number];

export interface VoiceIntent {
  intent: VoiceIntentName;
  /** `units` is present only on `logInsulin`, always a half-unit decimal
   *  string. Kept as a bag (not a discriminated union) so it matches the
   *  cycle-101 agent seam the answer layer already consumes. */
  slots: { units?: string };
}

/** Dose bounds. A spoken dose outside these — or one that is not a whole
 *  half-unit — is NOT clamped, it is REFUSED (→ `null`). Clamping a
 *  misheard "sixty" down to 50 would silently log a dose nobody said. */
export const MIN_DOSE_UNITS = 0.5;
export const MAX_DOSE_UNITS = 50;

// ─── normalization ─────────────────────────────────────────────────────

/** Lowercase, drop apostrophes (so "what's" → "whats"), turn every other
 *  non-alphanumeric-non-dot character into a space, collapse runs of
 *  whitespace.
 *
 *  A dot survives ONLY when it sits inside a number ("2.5"). A sentence-
 *  final dot is stripped — STT routinely punctuates, and "Yes." must match
 *  the confirm set exactly like "yes" does. (No lookbehind: some Android
 *  WebView builds in the fleet predate it.) */
export function normalizeTranscript(text: string): string {
  return text
    .toLowerCase()
    .replace(/[‘’']/g, "")
    .replace(/[^a-z0-9.]+/g, " ")
    // A dot not followed by a digit is punctuation, not a decimal point.
    .replace(/\.(?!\d)/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// ─── number words ──────────────────────────────────────────────────────

const ONES: Record<string, number> = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
  eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13,
  fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18,
  nineteen: 19,
};

const TENS: Record<string, number> = {
  twenty: 20, thirty: 30, forty: 40, fifty: 50,
};

/** "a"/"an" mean 1 ONLY directly before a unit word or "half" — otherwise
 *  "log a dose of two units" would parse the article as the dose. */
const ARTICLES = new Set(["a", "an"]);
const UNIT_WORDS = new Set(["unit", "units"]);

/** Sentinel for "a dose clearly started here but is not representable"
 *  (e.g. "six point three"). Distinct from `null` = "no dose here", so the
 *  caller can refuse the whole utterance instead of falling back to the
 *  whole-number prefix and logging 6 when the user said 6.3. */
const INVALID_DOSE = Number.NaN;

interface WholeParse {
  value: number;
  next: number;
}

function parseWholeAt(tokens: string[], i: number): WholeParse | null {
  const tok = tokens[i];
  if (tok == null) return null;
  if (TENS[tok] != null) {
    const ones = tokens[i + 1] != null ? ONES[tokens[i + 1]!] : undefined;
    // "twenty one" — only 1..9 compound; "twenty ten" is not a number.
    if (ones != null && ones >= 1 && ones <= 9) {
      return { value: TENS[tok]! + ones, next: i + 2 };
    }
    return { value: TENS[tok]!, next: i + 1 };
  }
  if (ONES[tok] != null) return { value: ONES[tok]!, next: i + 1 };
  if (/^\d+$/.test(tok)) return { value: Number(tok), next: i + 1 };
  return null;
}

/** Parse a dose starting exactly at `tokens[i]`.
 *  Returns the numeric value, `INVALID_DOSE` (NaN) when a dose started but
 *  cannot be represented, or `null` when no dose starts here. */
function parseDoseAt(tokens: string[], i: number): number | null {
  const tok = tokens[i];
  if (tok == null) return null;

  // "half a unit" / "half unit"
  if (tok === "half") {
    const nxt = tokens[i + 1];
    if (nxt != null && (ARTICLES.has(nxt) || UNIT_WORDS.has(nxt))) return 0.5;
    return null;
  }
  // "a half unit" / "a unit"
  if (ARTICLES.has(tok)) {
    const nxt = tokens[i + 1];
    if (nxt === "half") return 0.5;
    if (nxt != null && UNIT_WORDS.has(nxt)) return 1;
    return null;
  }
  // Digit form: "2.5", "6", "0.5".
  if (/^\d+\.\d+$/.test(tok)) return Number(tok);

  const whole = parseWholeAt(tokens, i);
  if (whole == null) return null;

  // "<n> and a half"
  if (
    tokens[whole.next] === "and" &&
    ARTICLES.has(tokens[whole.next + 1] ?? "") &&
    tokens[whole.next + 2] === "half"
  ) {
    return whole.value + 0.5;
  }
  // "<n> point <digit>" — only ".5" is representable; anything else is a
  // real dose the user said that we cannot honour, so refuse outright.
  if (tokens[whole.next] === "point") {
    const frac = tokens[whole.next + 1];
    if (frac == null) return INVALID_DOSE;
    const fracVal = ONES[frac] != null ? ONES[frac] : Number(frac);
    if (!Number.isFinite(fracVal)) return INVALID_DOSE;
    return fracVal === 5 ? whole.value + 0.5 : INVALID_DOSE;
  }
  return whole.value;
}

/** Format a validated dose as the seam's half-unit decimal string. */
function formatUnits(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function isRepresentableDose(n: number): boolean {
  return (
    Number.isFinite(n) &&
    n >= MIN_DOSE_UNITS &&
    n <= MAX_DOSE_UNITS &&
    Number.isInteger(n * 2)
  );
}

/**
 * Extract a dose from free text as a half-unit decimal string, or `null`.
 *
 * Scans left to right and takes the FIRST position that yields a dose. If
 * that dose is unrepresentable (out of range, or a non-half fraction), the
 * whole parse fails — it never silently falls back to a nearby number.
 */
export function parseDoseUnits(text: string): string | null {
  const tokens = normalizeTranscript(text).split(" ").filter(Boolean);
  for (let i = 0; i < tokens.length; i++) {
    const v = parseDoseAt(tokens, i);
    if (v == null) continue;
    if (!isRepresentableDose(v)) return null;
    return formatUnits(v);
  }
  return null;
}

// ─── the closed pattern set ────────────────────────────────────────────
//
// Each intent is a finite list of anchored regexes over the normalized
// transcript. Anchoring at both ends is what keeps the set closed: a
// phrase either IS one of these or it is not recognized. The shared
// wrapper allows a short, explicit list of leading politeness tokens and
// trailing tokens — nothing else.

const FILLER_PREFIX =
  "(?:hey sugalarm |sugalarm |hey |ok |okay |so |please |can you |could you |tell me |whats |i want to know )*";
const FILLER_SUFFIX = "(?: please| right now| now| for me| again)?";

function anchored(body: string): RegExp {
  return new RegExp(`^${FILLER_PREFIX}(?:${body})${FILLER_SUFFIX}$`);
}

// NOTE: the wrapper's prefix list includes a bare "whats", so bodies below
// spell the question form WITHOUT it where that reads naturally, and the
// bodies that do spell it out still match (the prefix group is optional).

const GLUCOSE_PATTERNS: RegExp[] = [
  anchored(
    "(?:whats|what is|hows|how is|how are) (?:my|the) (?:current |latest )?" +
      "(?:glucose|blood sugar|blood glucose|sugar|sugar level|number|reading|cgm)",
  ),
  anchored("(?:my |the )?(?:glucose|blood sugar|blood glucose)(?: level| reading| number)?"),
  anchored("(?:whats|what is) (?:my|the) (?:glucose|sugar) (?:level|reading|number)"),
  anchored("how (?:am i|is she|is he|are they) doing"),
  anchored("(?:read|give me|say) (?:me )?(?:my |the )?(?:glucose|blood sugar|number|reading)"),
];

const IOB_PATTERNS: RegExp[] = [
  anchored(
    "how much insulin (?:is |do i have |have i got )?(?:still )?(?:left )?on board",
  ),
  anchored("(?:how much )?insulin (?:is )?(?:left )?on board"),
  anchored("insulin left on board"),
  anchored("(?:whats|what is) (?:my|the) (?:iob|insulin on board)"),
  anchored("(?:my )?iob"),
  anchored("do i have (?:any )?insulin on board"),
  anchored("how much insulin (?:is|do i have) (?:left|active)"),
];

const TIME_LEFT_PATTERNS: RegExp[] = [
  anchored("how much time (?:is )?(?:left|remaining)(?: on my insulin)?"),
  anchored("how much (?:time|longer) (?:is left )?(?:until|till|til) (?:my )?insulin (?:clears|wears off|is gone)"),
  anchored("how (?:much )?long(?:er)? (?:until|till|til) (?:my )?insulin (?:clears|wears off|is gone|runs out)"),
  anchored("when will (?:my )?insulin (?:clear|wear off|be gone|run out)"),
  anchored("(?:how much )?time left on (?:my )?insulin"),
  anchored("how long is (?:my )?insulin active"),
  anchored("(?:whats|what is) (?:my )?insulin (?:time )?(?:left|remaining)"),
];

const LAST_DOSE_PATTERNS: RegExp[] = [
  anchored("(?:when|what) was my last (?:dose|shot|injection|bolus)"),
  anchored("(?:my )?last (?:dose|shot|injection|bolus)"),
  anchored("when did i last (?:take|have|give) (?:insulin|a dose|my insulin)"),
  anchored("how long ago was my last (?:dose|shot|injection|bolus)"),
  anchored("(?:whats|what is) my last (?:dose|shot|injection|bolus)"),
];

// Confirm answers are matched as EXACT normalized phrases (a closed list),
// not regexes — they are the shortest, highest-stakes utterances and a
// loose pattern here is what turns a "no" into a logged dose.
//
// "stop" is DELIBERATELY ABSENT from the no-set. It is alarm vocabulary,
// and this grammar must never carry alarm vocabulary — see the header.
const YES_PHRASES = new Set([
  "yes", "yeah", "yep", "yup", "ya", "yes please", "yes do it",
  "sure", "ok", "okay", "correct", "confirm", "confirmed", "affirmative",
  "do it", "go ahead", "thats right", "that is right", "sounds good",
]);

const NO_PHRASES = new Set([
  "no", "nope", "nah", "negative", "cancel", "cancel that", "no thanks",
  "no thank you", "dont", "do not", "never mind", "nevermind",
  "forget it", "thats wrong", "that is wrong",
]);

// ─── logInsulin: ANCHORED, like every other intent ─────────────────────
//
// An early draft gated logging on "contains a log verb AND contains
// insulin vocabulary AND a dose appears somewhere", and the test suite
// immediately caught why that is unsafe: **"no i want to log six units"
// logged six units.** A keyword-contains rule over an arbitrarily long
// sentence is fail-OPEN — the one posture this grammar may not have. So
// logging is matched by fully anchored templates too, and the dose is read
// out of a capture group rather than scanned for anywhere in the string.
//
// Consequence worth keeping: a sentence with ANY extra leading words (a
// negation, a hedge, a second clause) matches no template and is refused.
// That is the intended trade — a missed log costs one repeat, a wrongly
// parsed log writes a dose nobody asked for.

const ONES_ALT = Object.keys(ONES).join("|");
const TENS_ALT = Object.keys(TENS).join("|");
const SMALL_ALT = "one|two|three|four|five|six|seven|eight|nine";
// Decimals first — "\\d+" would otherwise swallow the integer part of 2.5.
const NUM_ALT =
  `\\d+\\.\\d+|\\d+|(?:${TENS_ALT})(?: (?:${SMALL_ALT}))?|${ONES_ALT}`;
// "an" before "a" so the longer article wins the alternation.
const DOSE_BODY =
  `(?:half a|a half|an|a|${NUM_ALT})(?: and a half| point (?:${ONES_ALT}|\\d+))?`;

/** Politeness tokens allowed before a LOG command. Deliberately narrower
 *  than the query filler — no "i want to" / "tell me", because admitting a
 *  free-form lead-in is what let a negated sentence through. */
const LOG_PREFIX = "(?:hey sugalarm |sugalarm |ok |okay |please |can you |could you )*";

const LOG_TEMPLATES: string[] = [
  // "log six units", "log 2.5 units of insulin", "log a bolus of two units"
  `(?:log|record|enter|add|take|give|inject) (?:a bolus of |a dose of |a shot of )?(?<dose>${DOSE_BODY}) units?(?: of insulin)?`,
  // "log two units insulin"
  `(?:log|record|enter|add|take|give|inject) (?<dose>${DOSE_BODY}) units? insulin`,
  // "log insulin six units"
  `(?:log|record|enter|add) insulin (?<dose>${DOSE_BODY}) units?`,
  // "i took two and a half units of insulin"
  `i (?:took|gave|had) (?<dose>${DOSE_BODY}) units?(?: of insulin)?`,
];

const LOG_PATTERNS: RegExp[] = LOG_TEMPLATES.map(
  (body) => new RegExp(`^${LOG_PREFIX}(?:${body})${FILLER_SUFFIX}$`),
);

// ─── the parser ────────────────────────────────────────────────────────

function matchesAny(patterns: RegExp[], s: string): boolean {
  return patterns.some((re) => re.test(s));
}

/**
 * Map a FINAL transcript to exactly one intent, or `null`.
 *
 * Unrecognized input returns `null` and the caller does nothing — the
 * grammar never guesses. Order is irrelevant to correctness (every query
 * pattern is fully anchored and a logging utterance can match none of
 * them), but confirms are checked first because they are exact-set matches
 * and cheapest.
 */
export function parseTranscript(text: string): VoiceIntent | null {
  if (typeof text !== "string") return null;
  const s = normalizeTranscript(text);
  if (s.length === 0) return null;

  if (YES_PHRASES.has(s)) return { intent: "confirmYes", slots: {} };
  if (NO_PHRASES.has(s)) return { intent: "confirmNo", slots: {} };

  if (matchesAny(GLUCOSE_PATTERNS, s)) return { intent: "getGlucose", slots: {} };
  // Time-left is checked before IOB: both talk about insulin, and the
  // time-left phrasings are the more specific of the two.
  if (matchesAny(TIME_LEFT_PATTERNS, s)) {
    return { intent: "getInsulinTimeLeft", slots: {} };
  }
  if (matchesAny(IOB_PATTERNS, s)) return { intent: "getIOB", slots: {} };
  if (matchesAny(LAST_DOSE_PATTERNS, s)) return { intent: "getLastDose", slots: {} };

  // logInsulin: an anchored template must match, AND the dose it captured
  // must be representable. Either missing → null (fail closed).
  for (const re of LOG_PATTERNS) {
    const m = re.exec(s);
    const dose = m?.groups?.dose;
    if (dose == null) continue;
    // The template already proved a unit word follows the capture; append
    // it so a bare article ("log a unit") resolves to 1 without loosening
    // `parseDoseUnits` itself.
    const units = parseDoseUnits(`${dose} units`);
    if (units != null) return { intent: "logInsulin", slots: { units } };
    // A matched template with an unrepresentable dose ("six point three",
    // "sixty") REFUSES outright — it never falls through to another
    // template that might read a different number out of the same words.
    return null;
  }

  return null;
}
